import { Pencil, Plus, Trash2 } from "lucide-react";
import { fmtDate, todayISO, useStore } from "../store";
import { useCrud } from "../useCrud";
import {
  Btn, Card, ConfirmDelete, Empty, Field, IconBtn, Modal, PageHead, Pill, Select, Table, Td, inputCls, statusTone,
} from "../ui";

const blank = () => ({ date: todayISO(), department: "", topic: "", output: "", notes: "", improvement: "", nextAction: "", actionStatus: "Pending" });
const validate = (f) => {
  const e = {};
  if (!f.date) e.date = "Date is required";
  if (!f.department) e.department = "Select a department";
  if (!f.topic?.trim()) e.topic = "Topic is required";
  return e;
};

const Area = ({ c, k, label, testid }) => (
  <Field label={label} className="sm:col-span-2">
    <textarea rows={2} className={inputCls} value={c.form[k]} onChange={c.change(k)} data-testid={testid} />
  </Field>
);

const MeetingForm = ({ c, departments }) => (
  <form onSubmit={c.save} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="meeting-form">
    <Field label="Date *" error={c.errors.date}><input type="date" className={inputCls} value={c.form.date} onChange={c.change("date")} data-testid="meeting-date-input" /></Field>
    <Field label="Department *" error={c.errors.department}>
      <Select options={departments} placeholder={departments.length ? "Select department" : "No departments — add in Settings"} value={c.form.department} onChange={c.change("department")} data-testid="meeting-department-select" />
    </Field>
    <Field label="Topic *" error={c.errors.topic} className="sm:col-span-2"><input className={inputCls} value={c.form.topic} onChange={c.change("topic")} data-testid="meeting-topic-input" /></Field>
    <Area c={c} k="output" label="Output / Result" testid="meeting-output-input" />
    <Area c={c} k="notes" label="Written Notes" testid="meeting-notes-input" />
    <Area c={c} k="improvement" label="Improvement Scope" testid="meeting-improvement-input" />
    <Area c={c} k="nextAction" label="Next Action" testid="meeting-next-action-input" />
    <Field label="Action Status"><Select options={["Pending", "Done"]} value={c.form.actionStatus} onChange={c.change("actionStatus")} data-testid="meeting-action-status-select" /></Field>
    <div className="flex items-end justify-end gap-2">
      <Btn type="button" variant="ghost" onClick={c.close} data-testid="meeting-cancel-btn">Cancel</Btn>
      <Btn type="submit" data-testid="meeting-save-btn">Save meeting</Btn>
    </div>
  </form>
);

const Cell = ({ v }) => <Td className="max-w-[220px] text-zinc-400">{v || "—"}</Td>;

export default function Meetings() {
  const { meetings, departments } = useStore();
  const c = useCrud("meetings", blank, validate, "Meeting");
  const deptOpts = [...new Set([...departments, c.form?.department].filter(Boolean))];
  const rows = [...meetings].sort((a, b) => b.date.localeCompare(a.date));
  return (
    <div>
      <PageHead title="Meetings" sub="Record every department meeting and its follow-up.">
        <Btn onClick={() => c.openNew()} data-testid="add-meeting-btn"><Plus className="h-4 w-4" />Add Meeting</Btn>
      </PageHead>
      <Card className="animate-rise [animation-delay:80ms]">
        {rows.length ? (
          <Table head={["Date", "Department", "Topic", "Output / Result", "Written Notes", "Improvement Scope", "Next Action", "Action Status", ""]}>
            {rows.map((m) => (
              <tr key={m.id} className="transition-colors hover:bg-white/[0.02]" data-testid={`meeting-row-${m.id}`}>
                <Td className="whitespace-nowrap">{fmtDate(m.date)}</Td>
                <Td className="whitespace-nowrap font-medium text-zinc-100">{m.department}</Td>
                <Td className="max-w-[200px] text-zinc-200">{m.topic}</Td>
                <Cell v={m.output} /><Cell v={m.notes} /><Cell v={m.improvement} /><Cell v={m.nextAction} />
                <Td><Pill tone={statusTone(m.actionStatus)}>{m.actionStatus}</Pill></Td>
                <Td className="whitespace-nowrap">
                  <IconBtn onClick={() => c.openEdit(m)} data-testid={`meeting-edit-${m.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                  <IconBtn onClick={() => c.setDelId(m.id)} className="hover:text-rose-400" data-testid={`meeting-delete-${m.id}`}><Trash2 className="h-4 w-4" /></IconBtn>
                </Td>
              </tr>
            ))}
          </Table>
        ) : <Empty testid="meetings-empty" title="No meetings recorded" hint="Add a meeting to capture its notes and next action." />}
      </Card>
      <Modal open={!!c.form} onClose={c.close} title={c.editId ? "Edit Meeting" : "Add Meeting"} testid="meeting-modal">
        {c.form && <MeetingForm c={c} departments={deptOpts} />}
      </Modal>
      <ConfirmDelete open={!!c.delId} onCancel={() => c.setDelId(null)} onConfirm={c.confirmDelete} what="this meeting" />
    </div>
  );
}
